import { makeAutoObservable } from 'mobx'
import {
	APPLICATIONS_NAV_BADGE_KEYS,
	ASSIGNMENTS_NAV_BADGE_KEY,
	BELL_ROW_ID_BY_KEY,
	MESSAGES_NAV_BADGE_KEY,
} from 'shared/config/typeNotifications'

const DEFAULT_COUNTS = {
	essays: 0,
	feedback: 0,
	documents: 0,
	recommendations: 0,
	lorRequests: 0,
	lorUploaded: 0,
	assignments: 0,
	newApplications: 0,
}

const formatBadgeText = count => {
	const n = Number(count) || 0
	if (!n) return ''
	return n > 9 ? '9+' : String(n)
}

const getNotificationPreferencesStore = () =>
	require('shared/stores/notificationPreferencesStore').notificationPreferencesStore

const notificationCount = (preferenceKey, rawValue) => {
	const store = getNotificationPreferencesStore()
	// Touch preferences so MobX recomputes notification getters when toggles change.
	const prefs = store.preferences
	if (prefs[preferenceKey] === false) {
		return 0
	}
	return Math.max(0, Number(rawValue) || 0)
}

class PrioritySummaryStore {
	counts = { ...DEFAULT_COUNTS }
	messagesUnreadTotal = 0
	fetchedOnce = false
	loadStatus = 'idle'
	_refreshFn = null

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true })
	}

	setRefreshFn(fn) {
		this._refreshFn = typeof fn === 'function' ? fn : null
	}

	async refresh() {
		if (this._refreshFn) {
			await this._refreshFn()
		}
	}

	setCounts(counts) {
		this.counts = { ...this.counts, ...(counts || {}) }
		this.fetchedOnce = true
		this.loadStatus = 'success'
	}

	setMessagesUnreadTotal(total) {
		this.messagesUnreadTotal = Math.max(0, Number(total) || 0)
	}

	setLoadStatus(status) {
		this.loadStatus = status || 'idle'
	}

	optimisticDecrement(key, amount = 1) {
		const current = Number(this.counts[key]) || 0
		const delta = Math.max(0, Number(amount) || 0)
		const next = Math.max(0, current - delta)
		if (next === current) return false
		this.counts = {
			...this.counts,
			[key]: next,
		}
		return true
	}

	optimisticMessagesDecrement(amount = 1) {
		const current = Number(this.messagesUnreadTotal) || 0
		const delta = Math.max(0, Number(amount) || 0)
		const next = Math.max(0, current - delta)
		if (next === current) return false
		this.messagesUnreadTotal = next
		return true
	}

	get applicationsBadgeCount() {
		const c = this.counts
		return (
			(Number(c.newApplications) || 0) +
			(Number(c.essays) || 0) +
			(Number(c.feedback) || 0) +
			(Number(c.documents) || 0) +
			(Number(c.recommendations) || 0)
		)
	}

	get applicationsBadgeText() {
		return formatBadgeText(this.applicationsBadgeCount)
	}

	get assignmentsBadgeText() {
		return formatBadgeText(this.counts.assignments)
	}

	get messagesBadgeText() {
		return formatBadgeText(this.messagesUnreadTotal)
	}

	get totalNotificationCount() {
		return (
			this.applicationsBadgeCount +
			(Number(this.counts.assignments) || 0) +
			(Number(this.messagesUnreadTotal) || 0)
		)
	}

	get bellBadgeText() {
		return formatBadgeText(this.totalNotificationCount)
	}

	get notificationApplicationsBadgeCount() {
		const c = this.counts
		return APPLICATIONS_NAV_BADGE_KEYS.reduce(
			(sum, key) => sum + notificationCount(key, c[key]),
			0,
		)
	}

	get notificationApplicationsBadgeText() {
		return formatBadgeText(this.notificationApplicationsBadgeCount)
	}

	get notificationAssignmentsBadgeText() {
		return formatBadgeText(
			notificationCount(ASSIGNMENTS_NAV_BADGE_KEY, this.counts.assignments),
		)
	}

	get notificationMessagesBadgeText() {
		return formatBadgeText(
			notificationCount(MESSAGES_NAV_BADGE_KEY, this.messagesUnreadTotal),
		)
	}

	get notificationBellBadgeText() {
		const total =
			this.notificationApplicationsBadgeCount +
			notificationCount(ASSIGNMENTS_NAV_BADGE_KEY, this.counts.assignments) +
			notificationCount(MESSAGES_NAV_BADGE_KEY, this.messagesUnreadTotal)
		return formatBadgeText(total)
	}

	get notificationSummaryRows() {
		const c = this.counts
		const rows = [
			{
				id: BELL_ROW_ID_BY_KEY.newApplications,
				label: 'New Applications',
				count: notificationCount('newApplications', c.newApplications),
				action: 'applications',
			},
			{
				id: BELL_ROW_ID_BY_KEY.essays,
				label: 'Essays Waiting Review',
				count: notificationCount('essays', c.essays),
				action: 'priority',
				tab: 'essays',
			},
			{
				id: BELL_ROW_ID_BY_KEY.feedback,
				label: 'Feedback Replies',
				count: notificationCount('feedback', c.feedback),
				action: 'priority',
				tab: 'feedback',
			},
			{
				id: BELL_ROW_ID_BY_KEY.documents,
				label: 'Documents Uploaded',
				count: notificationCount('documents', c.documents),
				action: 'priority',
				tab: 'documents',
			},
			{
				id: BELL_ROW_ID_BY_KEY.lorRequests,
				label: 'LoR Requests',
				count: notificationCount('lorRequests', c.lorRequests),
				action: 'priority',
				tab: 'recommendations',
			},
			{
				id: BELL_ROW_ID_BY_KEY.lorUploaded,
				label: 'Uploaded LoR',
				count: notificationCount('lorUploaded', c.lorUploaded),
				action: 'priority',
				tab: 'recommendations',
			},
			{
				id: BELL_ROW_ID_BY_KEY.assignments,
				label: 'Assignment Updates',
				count: notificationCount('assignments', c.assignments),
				action: 'classroom_new',
			},
			{
				id: BELL_ROW_ID_BY_KEY.messages,
				label: 'Student Messages',
				count: notificationCount('messages', this.messagesUnreadTotal),
				action: 'messages',
			},
		]
		return rows.filter(row => row.count > 0)
	}
}

export const prioritySummaryStore = new PrioritySummaryStore()
